import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useComments } from "@/hooks/useSupabaseData";

interface CommentSystemProps {
  newsId: string;
}

export default function CommentSystem({ newsId }: CommentSystemProps) {
  const { user, profile, signInWithGoogle } = useAuth();
  const { comments, loading, addComment, likeComment, reportComment } = useComments(newsId);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportModal, setReportModal] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [reportSent, setReportSent] = useState(false);
  const [showPendingInfo, setShowPendingInfo] = useState(false);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    if (!user || !profile) {
      setShowLogin(true);
      return;
    }
    setSubmitting(true);
    await addComment(
      newComment,
      user.id,
      profile.name || user.email?.split("@")[0] || "Usuário",
      profile.avatar
    );
    setNewComment("");
    setShowPendingInfo(true);
    setTimeout(() => setShowPendingInfo(false), 5000);
    setSubmitting(false);
  };

  const handleAddReply = async (parentId: string) => {
    if (!replyText.trim()) return;
    if (!user || !profile) {
      setShowLogin(true);
      return;
    }
    setSubmitting(true);
    await addComment(
      replyText,
      user.id,
      profile.name || user.email?.split("@")[0] || "Usuário",
      profile.avatar,
      parentId
    );
    setReplyText("");
    setReplyTo(null);
    setShowPendingInfo(true);
    setTimeout(() => setShowPendingInfo(false), 5000);
    setSubmitting(false);
  };

  const handleLike = async (commentId: string) => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    await likeComment(commentId, user.id);
  };

  const handleReport = async () => {
    if (!reportModal || !user) return;
    setSubmitting(true);
    const success = await reportComment(reportModal, user.id, reportReason);
    if (success) {
      setReportSent(true);
      setTimeout(() => {
        setReportSent(false);
        setReportModal(null);
        setReportReason("");
      }, 2000);
    }
    setSubmitting(false);
  };

  const timeAgo = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return then.toLocaleDateString("pt-BR");
  };

  const topLevelComments = comments.filter((c) => !c.parent_id);
  const getReplies = (parentId: string) => comments.filter((c) => c.parent_id === parentId);

  return (
    <div className="w-full">
      <h3 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black mb-6">
        Comentários ({comments.length})
      </h3>

      {/* Pending Info */}
      {showPendingInfo && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-50 border border-yellow-200 flex items-start gap-3">
          <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
          <div>
            <p className="text-sm font-medium text-yellow-800">Comentário enviado para moderação</p>
            <p className="text-xs text-yellow-600 mt-0.5">Seu comentário está sob análise e será publicado em breve.</p>
          </div>
        </div>
      )}

      {/* Login Required Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-lg">Faça login para comentar</h4>
              <button onClick={() => setShowLogin(false)} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-alvinegro-muted text-sm mb-6">
              É necessário fazer login para comentar, curtir e responder comentários.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => { signInWithGoogle(); setShowLogin(false); }}
                className="w-full flex items-center justify-center gap-2 bg-white border border-alvinegro-border py-3 rounded-full hover:bg-alvinegro-light transition-colors cursor-pointer"
              >
                <i className="ri-google-fill text-lg"></i>
                <span className="text-sm font-medium">Entrar com Google</span>
              </button>
              <button onClick={() => setShowLogin(false)} className="w-full text-sm text-alvinegro-muted py-2 cursor-pointer">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-lg">Denunciar comentário</h4>
              <button onClick={() => { setReportModal(null); setReportSent(false); setReportReason(""); }} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            {reportSent ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-green-700">Denúncia enviada com sucesso!</p>
                <p className="text-xs text-alvinegro-muted mt-1">Obrigado por ajudar a manter a comunidade saudável.</p>
              </div>
            ) : (
              <>
                <p className="text-alvinegro-muted text-sm mb-4">
                  Informe o motivo da denúncia. Nossa equipe de moderação irá analisar.
                </p>
                <div className="space-y-2 mb-4">
                  {['Spam', 'Conteúdo ofensivo', 'Discurso de ódio', 'Informação falsa', 'Outro'].map((reason) => (
                    <button
                      key={reason}
                      onClick={() => setReportReason(reason)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        reportReason === reason
                          ? 'bg-alvinegro-black text-white'
                          : 'bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setReportModal(null); setReportReason(""); }}
                    className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleReport}
                    disabled={!reportReason || submitting}
                    className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {submitting ? 'Enviando...' : 'Denunciar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Comment */}
      <div className="bg-alvinegro-light rounded-2xl p-4 md:p-6 mb-8">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-alvinegro-border flex items-center justify-center shrink-0">
            {profile?.avatar ? (
              <img src={profile.avatar} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <i className="ri-user-line text-alvinegro-muted"></i>
            )}
          </div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "Escreva seu comentário..." : "Faça login para comentar..."}
              className="w-full px-4 py-3 rounded-xl border border-alvinegro-border bg-white text-sm resize-none focus:outline-none focus:border-alvinegro-black"
              rows={3}
              maxLength={500}
              disabled={submitting}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-alvinegro-muted">{newComment.length}/500</span>
              <button
                onClick={handleAddComment}
                disabled={submitting || !newComment.trim()}
                className="bg-alvinegro-black text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {submitting ? "Enviando..." : "Comentar"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      {loading ? (
        <div className="space-y-6 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-alvinegro-light shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-alvinegro-light rounded w-1/4"></div>
                <div className="h-3 bg-alvinegro-light rounded w-full"></div>
                <div className="h-3 bg-alvinegro-light rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {topLevelComments.map((comment) => (
            <div key={comment.id} className="border-b border-alvinegro-border pb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-alvinegro-black flex items-center justify-center shrink-0 overflow-hidden">
                  {comment.user_avatar ? (
                    <img src={comment.user_avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-sm">{comment.user_name.substring(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-alvinegro-black">{comment.user_name}</span>
                    <span className="text-xs text-alvinegro-muted">{timeAgo(comment.created_at)}</span>
                    {comment.status === 'denunciado' && (
                      <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs border border-red-200">Denunciado</span>
                    )}
                  </div>
                  <p className="text-sm text-alvinegro-dark leading-relaxed mb-3">{comment.content}</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLike(comment.id)}
                      className="flex items-center gap-1 text-xs text-alvinegro-muted hover:text-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-heart-line"></i>
                      <span>{comment.likes || 0}</span>
                    </button>
                    <button
                      onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                      className="flex items-center gap-1 text-xs text-alvinegro-muted hover:text-alvinegro-black transition-colors cursor-pointer"
                    >
                      <i className="ri-reply-line"></i>
                      <span>Responder</span>
                    </button>
                    <button
                      onClick={() => setReportModal(comment.id)}
                      className="flex items-center gap-1 text-xs text-alvinegro-muted hover:text-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-flag-line"></i>
                      <span>Denunciar</span>
                    </button>
                  </div>

                  {/* Reply Input */}
                  {replyTo === comment.id && (
                    <div className="mt-3 flex items-start gap-2">
                      <div className="w-8 h-8 rounded-full bg-alvinegro-border flex items-center justify-center shrink-0">
                        {profile?.avatar ? (
                          <img src={profile.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-xs text-alvinegro-muted"></i>
                        )}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Escreva sua resposta..."
                          className="w-full px-3 py-2 rounded-lg border border-alvinegro-border bg-white text-sm resize-none focus:outline-none focus:border-alvinegro-black"
                          rows={2}
                          maxLength={500}
                          disabled={submitting}
                        />
                        <div className="flex items-center justify-end mt-2 gap-2">
                          <button
                            onClick={() => setReplyTo(null)}
                            className="text-xs text-alvinegro-muted hover:text-alvinegro-black transition-colors cursor-pointer"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleAddReply(comment.id)}
                            disabled={submitting || !replyText.trim()}
                            className="bg-alvinegro-black text-white px-4 py-1.5 rounded-full text-xs font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            {submitting ? "Enviando..." : "Responder"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {(() => {
                    const replies = getReplies(comment.id);
                    return replies.length > 0 ? (
                      <div className="mt-4 space-y-3 pl-4 border-l-2 border-alvinegro-border">
                        {replies.map((reply) => (
                          <div key={reply.id} className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center shrink-0 overflow-hidden">
                              {reply.user_avatar ? (
                                <img src={reply.user_avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-alvinegro-dark font-bold text-xs">{reply.user_name.substring(0, 2).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-xs text-alvinegro-black">{reply.user_name}</span>
                                <span className="text-xs text-alvinegro-muted">{timeAgo(reply.created_at)}</span>
                              </div>
                              <p className="text-xs text-alvinegro-dark leading-relaxed mb-2">{reply.content}</p>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleLike(reply.id)}
                                  className="flex items-center gap-1 text-xs text-alvinegro-muted hover:text-red-600 transition-colors cursor-pointer"
                                >
                                  <i className="ri-heart-line"></i>
                                  <span>{reply.likes || 0}</span>
                                </button>
                                <button
                                  onClick={() => setReportModal(reply.id)}
                                  className="flex items-center gap-1 text-xs text-alvinegro-muted hover:text-red-600 transition-colors cursor-pointer"
                                >
                                  <i className="ri-flag-line"></i>
                                  <span>Denunciar</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          ))}
          {topLevelComments.length === 0 && (
            <div className="text-center py-8 text-alvinegro-muted">
              <i className="ri-chat-3-line text-4xl mb-2"></i>
              <p className="text-sm">Seja o primeiro a comentar!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}